// LatenciaTrend — gráfico de latencia última 7 días por servicio.
// Datos: vista v_health_latencia_7d (avg latencia por hora x servicio).
// Sin dependencias externas — SVG manual.

import { useEffect, useState, useMemo } from 'react'
import { supabase } from '../lib/supabase'

const COLORES = ['#1A3461', '#00A8B5', '#DC2626', '#D97706', '#7C3AED', '#16A34A']

export default function LatenciaTrend() {
  const [data, setData] = useState([])
  const [servicios, setServicios] = useState([])
  const [seleccionados, setSeleccionados] = useState([])
  const [loading, setLoading] = useState(true)
  const [hover, setHover] = useState(null) // {x, y, label, valores: [{nombre, color, valor}]}

  useEffect(() => {
    let cancel = false
    async function cargar() {
      const { data: rows } = await supabase
        .from('v_health_latencia_7d')
        .select('*')
        .order('hora', { ascending: true })

      if (cancel) return
      const servs = [...new Set((rows || []).map(r => r.servicio_id))]
      setServicios(servs.map(id => {
        const r = (rows || []).find(x => x.servicio_id === id)
        return { id, nombre: r?.servicio_nombre || id }
      }))

      // Por default top 4 con más muestras
      const conteo = {}
      ;(rows || []).forEach(r => { conteo[r.servicio_id] = (conteo[r.servicio_id] || 0) + r.muestras })
      const top4 = Object.entries(conteo)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
        .map(([id]) => id)
      setSeleccionados(top4)

      setData(rows || [])
      setLoading(false)
    }
    cargar()
    return () => { cancel = true }
  }, [])

  // Genera serie pivotada: [{hora, srv_id_1: ms, srv_id_2: ms, ...}, ...]
  const serie = useMemo(() => {
    const horas = [...new Set(data.map(r => r.hora))].sort()
    return horas.map(hora => {
      const row = { hora }
      seleccionados.forEach(srv => {
        const m = data.find(r => r.hora === hora && r.servicio_id === srv)
        row[srv] = m?.latencia_avg_ms ?? null
      })
      return row
    })
  }, [data, seleccionados])

  function toggleServicio(id) {
    if (seleccionados.includes(id)) {
      setSeleccionados(seleccionados.filter(s => s !== id))
    } else if (seleccionados.length < 6) {
      setSeleccionados([...seleccionados, id])
    }
  }

  function fmtHora(ts) {
    if (!ts) return ''
    const d = new Date(ts)
    return d.toLocaleString('es-UY', { day: '2-digit', month: 'short', hour: '2-digit' })
  }

  // ─── Dimensiones del chart ───
  const W = 880, H = 280
  const PAD_L = 48, PAD_R = 16, PAD_T = 12, PAD_B = 36
  const innerW = W - PAD_L - PAD_R
  const innerH = H - PAD_T - PAD_B

  // Cálculo de máxima latencia para escala Y
  const maxLatencia = useMemo(() => {
    let max = 0
    serie.forEach(row => {
      seleccionados.forEach(srv => {
        if (row[srv] != null && row[srv] > max) max = row[srv]
      })
    })
    return Math.max(max, 100) // mínimo 100ms para que la escala se vea
  }, [serie, seleccionados])

  // Helpers para convertir datos en coords SVG
  const xFor = (i) => PAD_L + (serie.length > 1 ? (i * innerW / (serie.length - 1)) : innerW / 2)
  const yFor = (ms) => PAD_T + innerH - (ms / maxLatencia) * innerH

  // Generar gridlines Y (cada 25% del rango)
  const gridY = [0, 0.25, 0.5, 0.75, 1].map(pct => ({
    y: PAD_T + innerH - pct * innerH,
    label: Math.round(maxLatencia * pct) + 'ms',
  }))

  // Etiquetas X — mostrar solo algunas para no saturar
  const numEtiquetas = Math.min(6, serie.length)
  const stepEtiqueta = serie.length > 1 ? Math.max(1, Math.floor(serie.length / numEtiquetas)) : 1
  const etiquetasX = serie
    .map((r, i) => (i % stepEtiqueta === 0 || i === serie.length - 1) ? { i, label: fmtHora(r.hora) } : null)
    .filter(Boolean)

  // Tooltip al hover
  function onMouseMove(e) {
    if (serie.length === 0 || !e.currentTarget) return
    const rect = e.currentTarget.getBoundingClientRect()
    const xPx = e.clientX - rect.left
    const xPct = (xPx - PAD_L) / innerW
    const i = Math.round(xPct * (serie.length - 1))
    if (i < 0 || i >= serie.length) { setHover(null); return }
    const row = serie[i]
    setHover({
      i,
      x: xFor(i),
      label: fmtHora(row.hora),
      valores: seleccionados
        .map((srv, idx) => {
          const valor = row[srv]
          if (valor == null) return null
          const meta = servicios.find(s => s.id === srv)
          return { nombre: meta?.nombre || srv, color: COLORES[idx % COLORES.length], valor }
        })
        .filter(Boolean),
    })
  }
  function onMouseLeave() { setHover(null) }

  return (
    <section className="infra-card">
      <h2>📈 Latencia 7 días</h2>
      <p>Promedio de latencia por hora de cada servicio. Útil para detectar degradaciones progresivas antes que algo caiga.</p>

      {loading && <div className="lt-empty">Cargando datos…</div>}
      {!loading && servicios.length === 0 && (
        <div className="lt-empty">
          Sin datos todavía. El cron <code>alertador-sistema-cada-5min</code> los está empezando a registrar.
          <br />Volvé en 1-2 horas para ver el primer trend.
        </div>
      )}

      {!loading && servicios.length > 0 && (
        <>
          {/* Selector de servicios */}
          <div className="lt-selector">
            {servicios.map((s) => {
              const idx = seleccionados.indexOf(s.id)
              const color = idx >= 0 ? COLORES[idx % COLORES.length] : '#CBD5E1'
              return (
                <button
                  key={s.id}
                  className={`lt-chip ${idx >= 0 ? 'is-active' : ''}`}
                  onClick={() => toggleServicio(s.id)}
                  style={{ borderColor: color, color: idx >= 0 ? color : '#94A3B8' }}
                >
                  <span className="lt-chip-dot" style={{ background: color }} />
                  {s.nombre}
                </button>
              )
            })}
          </div>

          {/* SVG chart */}
          {serie.length > 0 && seleccionados.length > 0 && (
            <div className="lt-svg-wrap">
              <svg
                viewBox={`0 0 ${W} ${H}`}
                width="100%"
                height={H}
                preserveAspectRatio="none"
                onMouseMove={onMouseMove}
                onMouseLeave={onMouseLeave}
                style={{ display: 'block', background: '#FAFBFC', borderRadius: 8 }}
              >
                {/* Gridlines Y + labels */}
                {gridY.map((g, i) => (
                  <g key={i}>
                    <line
                      x1={PAD_L} x2={W - PAD_R}
                      y1={g.y} y2={g.y}
                      stroke="#E2E8F0" strokeDasharray="3 3" strokeWidth="1"
                    />
                    <text
                      x={PAD_L - 6} y={g.y + 3}
                      textAnchor="end" fontSize="10" fill="#94A3B8"
                    >{g.label}</text>
                  </g>
                ))}

                {/* Etiquetas X */}
                {etiquetasX.map((e, i) => (
                  <text
                    key={i}
                    x={xFor(e.i)} y={H - 16}
                    textAnchor="middle" fontSize="9.5" fill="#94A3B8"
                  >{e.label}</text>
                ))}

                {/* Líneas por servicio */}
                {seleccionados.map((srv, idx) => {
                  const color = COLORES[idx % COLORES.length]
                  // Crear path saltando nulls
                  const segmentos = []
                  let actual = []
                  serie.forEach((row, i) => {
                    if (row[srv] != null) {
                      actual.push(`${xFor(i)},${yFor(row[srv])}`)
                    } else {
                      if (actual.length > 0) { segmentos.push(actual); actual = [] }
                    }
                  })
                  if (actual.length > 0) segmentos.push(actual)
                  return (
                    <g key={srv}>
                      {segmentos.map((seg, si) => (
                        <polyline
                          key={si}
                          points={seg.join(' ')}
                          fill="none"
                          stroke={color}
                          strokeWidth="2"
                          strokeLinejoin="round"
                          strokeLinecap="round"
                        />
                      ))}
                    </g>
                  )
                })}

                {/* Línea vertical hover + tooltip */}
                {hover && (
                  <g>
                    <line
                      x1={hover.x} x2={hover.x}
                      y1={PAD_T} y2={H - PAD_B}
                      stroke="#94A3B8" strokeWidth="1" strokeDasharray="2 2"
                    />
                    {hover.valores.map((v, i) => {
                      const row = serie[hover.i]
                      const y = yFor(row[seleccionados[i]] ?? 0)
                      return (
                        <circle
                          key={i}
                          cx={hover.x} cy={y}
                          r="4" fill={v.color} stroke="#fff" strokeWidth="2"
                        />
                      )
                    })}
                  </g>
                )}
              </svg>

              {/* Tooltip HTML por encima del SVG */}
              {hover && hover.valores.length > 0 && (
                <div
                  className="lt-tooltip"
                  style={{
                    left: `${(hover.x / W) * 100}%`,
                    transform: hover.x > W / 2 ? 'translateX(-100%) translateX(-10px)' : 'translateX(10px)',
                  }}
                >
                  <div className="lt-tooltip-titulo">{hover.label}</div>
                  {hover.valores.map((v, i) => (
                    <div key={i} className="lt-tooltip-row">
                      <span className="lt-tooltip-dot" style={{ background: v.color }} />
                      <span className="lt-tooltip-srv">{v.nombre}</span>
                      <span className="lt-tooltip-val">{v.valor} ms</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <style>{`
        .lt-empty { padding: 24px; text-align: center; color: #94A3B8; font-size: 13px; line-height: 1.6; }
        .lt-empty code { background: #F1F5F9; padding: 1px 6px; border-radius: 4px; font-family: 'SF Mono', monospace; font-size: 12px; color: #1A3461; }
        .lt-selector { display: flex; gap: 6px; flex-wrap: wrap; margin: 10px 0 12px; }
        .lt-chip {
          display: inline-flex; align-items: center; gap: 6px;
          background: #fff; border: 1.5px solid #E2E8F0;
          border-radius: 999px; padding: 4px 10px;
          font-size: 11px; font-weight: 600;
          cursor: pointer; font-family: inherit;
          transition: all 0.12s;
        }
        .lt-chip:hover { transform: translateY(-1px); }
        .lt-chip-dot { width: 8px; height: 8px; border-radius: 50%; }
        .lt-svg-wrap { position: relative; }
        .lt-tooltip {
          position: absolute; top: 12px;
          background: #fff; border: 1px solid #E2E8F0;
          border-radius: 8px; padding: 8px 10px;
          font-size: 11px; box-shadow: 0 4px 12px rgba(15,27,54,0.08);
          pointer-events: none; min-width: 140px;
        }
        .lt-tooltip-titulo { font-weight: 700; color: #1A3461; margin-bottom: 4px; }
        .lt-tooltip-row { display: flex; align-items: center; gap: 6px; padding: 2px 0; }
        .lt-tooltip-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .lt-tooltip-srv { flex: 1; color: #64748B; }
        .lt-tooltip-val { font-weight: 700; color: #1A3461; font-variant-numeric: tabular-nums; }
      `}</style>
    </section>
  )
}
